/**
 * Onboarding Firestore Converters (Client SDK)
 *
 * Client-side converters for use with firebase/firestore.
 * Handles Onboarding (parent) and OnboardingVersion (child) entities.
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type {
    ThemeV2Colors,
    ThemeV2Fonts,
    ThemeV2OptionsListSettings,
    ThemeV2LayoutSettings,
    ThemeV2AnimationSettings,
    GeneratedThemeV2Metadata,
    Backdrop,
    HeaderBar,
    ActionBar,
    FlowDocument,
} from '@modules/themes/types/themesV2.types';

export interface OnboardingVersionMetadata {
    schemaVersion?: string;
    defaultLanguage?: string;
    languages?: string[];
    launchScreenId?: string;
    metadata?: Record<string, unknown>;
    screens?: Record<string, unknown>;
    [key: string]: unknown;
}

export type LegacyThemeBlock = Record<string, unknown>;

export interface LegacyThemeColors {
    buttonsBackground: string;
    titlesSubtitles: string;
    buttonsTitle: string;
}

export interface LegacyThemeTextStyle {
    fontFamily?: string;
    fontWeight?: number | string;
    fontSize?: number;
    color?: string;
    [key: string]: unknown;
}

export interface LegacyThemeFonts {
    main: {
        fontFamily: string;
    };
    title: LegacyThemeTextStyle;
    subtitle: LegacyThemeTextStyle;
}

export interface LegacyThemeTableSettings {
    checkboxKind: 'circle' | 'square';
    tableBorderRadius: number;
    collectionBorderRadius: number;
    itemTitle: LegacyThemeTextStyle;
    itemSubtitle: LegacyThemeTextStyle;
}

// ============================================================================
// Theme Settings for Onboarding (Subcollection)
// ============================================================================

/**
 * Theme settings stored in subcollection for performance optimization.
 *
 * Path: projects/{projectId}/onboardings/{onboardingId}/themeSettings/settings
 *
 * These settings are extracted from Theme during wizard Step 3
 * and stored in a separate subcollection to:
 * - Avoid loading heavy data with every list query
 * - Enable lazy loading only when canvas/editing is opened
 * - Use as templates when adding new screens on canvas
 * - Edit theme settings from canvas without creating new version
 */
export interface OnboardingThemeSettings {
    /** Color palette */
    colors: LegacyThemeColors;
    /** Font configuration */
    fonts: LegacyThemeFonts;
    /** Table/list appearance */
    tableSettings: LegacyThemeTableSettings;
    /** Background style for screens */
    background: LegacyThemeBlock;
    /** Navigation bar configuration */
    navigationBar: LegacyThemeBlock;
    /** Footer with buttons */
    footer: LegacyThemeBlock;
    /** Original theme ID this was cloned from (for tracking) */
    sourceThemeId?: string;
    /** Retired V1 theme templates retained only for legacy data reads. */
    screenGraph?: OnboardingVersionMetadata;
}

// ============================================================================
// V2 Theme Settings for Onboarding (Subcollection)
// ============================================================================

/**
 * V2 theme settings stored in subcollection for performance optimization.
 *
 * Path: projects/{projectId}/onboardings/{onboardingId}/themeSettingsV2/settings
 *
 * Uses V2 types: Backdrop (not BackgroundStyle), HeaderBar (not NavigationBar),
 * ActionBar, TypographyStyle, and FlowDocument.
 */
export interface OnboardingThemeSettingsV2 {
    /** Color palette (same 3 roles as V1) */
    colors: ThemeV2Colors;
    /** Font configuration (uses TypographyStyle, align: start|center|end) */
    fonts: ThemeV2Fonts;
    /** Options/list appearance (replaces V1 tableSettings) */
    optionsListSettings: ThemeV2OptionsListSettings;
    /** Layout, spacing, radius, and dimension tokens */
    layoutSettings?: ThemeV2LayoutSettings;
    /** Motion scheme (screen transition + entrance). Absent = no theme motion */
    animationSettings?: ThemeV2AnimationSettings;
    /** Background (V2 Backdrop: solid|photo|clip) */
    background: Backdrop;
    /** Browser chrome color inherited by theme template screens; null disables it */
    webViewBackgroundColor?: string | null;
    /** Header bar (V2 HeaderBar: skip/back/close/progressIndicator) */
    headerBar: HeaderBar;
    /** Action bar (V2 ActionBar: primary/secondary buttons) */
    actionBar: ActionBar;
    /** Original theme ID this was cloned from (for tracking) */
    sourceThemeId?: string;
    /** V2 screen templates (FlowDocument with StepNode screens) */
    flowDocument?: FlowDocument;
    /** Optional metadata for AI/image-derived theme snapshots */
    generatedMetadata?: GeneratedThemeV2Metadata;
}

// ============================================================================
// Onboarding Interfaces
// ============================================================================

export interface Onboarding {
    id: string;
    projectId: string;
    name: string;
    createdAt: string; // ISO date string
    updatedAt: string; // ISO date string
    latestVersionId?: string;
    publishedVersionId?: string;
    folderId?: string | null; // Reference to OnboardingFolder, null = root level
    /** Reference to source theme ID (for tracking origin, not storing data) */
    sourceThemeId?: string | null;
    /**
     * Which list `sourceThemeId` names: a project theme or a system theme (`themesV2`). Set by the
     * admin theme switch; absent on older onboardings (match the id across both lists then).
     */
    sourceThemeKind?: 'project' | 'system' | null;
    /**
     * Default language for the onboarding (source of truth).
     * All versions should sync to this value.
     * Optional for backward compatibility with existing data.
     */
    defaultLanguage?: string;
    /**
     * Supported languages for the onboarding (source of truth).
     * All versions should sync to this value.
     * Must include defaultLanguage.
     * Optional for backward compatibility with existing data.
     */
    languages?: string[];
    /** Target platform (design-time hint for previews and locale resolution) */
    platform?: 'ios' | 'android' | 'web';
    /** Schema version indicator: 'v1' for legacy, 'v2' for converted/new V2 onboardings */
    screenGraphType?: 'v1' | 'v2';
    /** Reference to original V1 onboarding ID (set during V1→V2 conversion for tracking) */
    parentId?: string;
    /**
     * Document kind discriminator (v2.19.0). Absent = classic funnel onboarding.
     * 'article' = article root document (reuses the full Onboarding+versions
     * model in the SAME collection; funnel lists/pickers must exclude it).
     */
    kind?: 'article';
}

export interface OnboardingVersion {
    id: string;
    onboardingId: string;
    name: string;
    /** U8 (Spec D2): operator note on what changed in this version. */
    description?: string;
    createdAt: string; // ISO date string
    createUserId: string;
    parentVersionId?: string;
    /**
     * Version metadata container kept under the historical Firestore field name
     * until the external schema/data migration removes or renames it.
     */
    screensGraph: OnboardingVersionMetadata;
    /** V2 FunnelVariable definitions — stored at version level, not in screensGraph. V2 only. */
    variables?: Record<string, unknown>;

    // ── Two-axis versioning / freeze-on-publish (Spec A) ──
    /**
     * True once this version has shipped through a publish. A frozen version is
     * immutable: an edit mints a NEW version first (see V2ScreenFlowCoordinator),
     * so the shipped experiment pin stays byte-stable.
     */
    frozen?: boolean;
    /** ISO timestamp when this version was published/frozen. */
    publishedAt?: string;
    /** Strategy this onboarding version was compiled from (provenance for the pin-chain). */
    sourceStrategyId?: string;
    /** The strategy versionId (strategies/{id}/versions/{versionId}) that produced this onboarding version. */
    sourceStrategyVersion?: string;
}

export const onboardingConverter: FirestoreDataConverter<Onboarding> = {
    toFirestore(onboarding: Onboarding): any {
        const result: any = { ...onboarding };
        // Remove id from data if it's there (Firestore keeps it in doc metadata)
        delete result.id;

        if (result.createdAt) {
            result.createdAt = Timestamp.fromDate(new Date(result.createdAt));
        }
        if (result.updatedAt) {
            result.updatedAt = Timestamp.fromDate(new Date(result.updatedAt));
        }

        if (result.latestVersionId === undefined) {
            delete result.latestVersionId;
        }
        if (result.publishedVersionId === undefined) {
            delete result.publishedVersionId;
        }
        // Handle sourceThemeId - remove if undefined, keep null (explicit no theme)
        if (result.sourceThemeId === undefined) {
            delete result.sourceThemeId;
        }

        // Handle language fields - remove if undefined
        if (result.defaultLanguage === undefined) {
            delete result.defaultLanguage;
        }
        if (result.languages === undefined) {
            delete result.languages;
        }
        if (result.platform === undefined) {
            delete result.platform;
        }
        if (result.screenGraphType === undefined) {
            delete result.screenGraphType;
        }
        if (result.parentId === undefined) {
            delete result.parentId;
        }
        if (result.kind === undefined) {
            delete result.kind;
        }

        return result;
    },

    fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): Onboarding {
        const data = snapshot.data(options);
        const result: any = { ...data, id: snapshot.id };

        if (result.createdAt?.toDate) {
            result.createdAt = result.createdAt.toDate().toISOString();
        }
        if (result.updatedAt?.toDate) {
            result.updatedAt = result.updatedAt.toDate().toISOString();
        }

        return result as Onboarding;
    }
};

export const onboardingVersionConverter: FirestoreDataConverter<OnboardingVersion> = {
    toFirestore(version: OnboardingVersion): any {
        const result: any = { ...version };
        delete result.id;

        if (result.createdAt) {
            result.createdAt = Timestamp.fromDate(new Date(result.createdAt));
        }

        // Version metadata is stored directly as a map. V2 screen documents live
        // in the screensV2 subcollection, not inside this field.

        if (result.parentVersionId === undefined) {
            delete result.parentVersionId;
        }
        if (result.description === undefined || result.description === '') {
            delete result.description;
        }

        return result;
    },

    fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): OnboardingVersion {
        const data = snapshot.data(options);
        const result: any = { ...data, id: snapshot.id };

        if (result.createdAt?.toDate) {
            result.createdAt = result.createdAt.toDate().toISOString();
        }

        return result as OnboardingVersion;
    }
};
